import { Tooltip } from "@mixitone/components";
import CalendarIcon from "@mixitone/icons/calendar.svg";
import UserIcon from "@mixitone/icons/user.svg";
import { ApplicationView } from "@mixitone/mvc";
import { dateAndTime, formatDate } from "@mixitone/util";
import React from "react";
import { Link } from "react-router-dom";
import { RegistrationResponse } from "../controllers/PlayerCompetitionsPageController";

interface CompetitionItemProps {
  registration: RegistrationResponse;
}

const statusColor: Record<string, string> = {
  completed: "bg-green-50 text-green-700 ring-green-700/10",
  withdrawn: "bg-red-50 text-red-700 ring-red-700/10",
  payment_required: "bg-yellow-50 text-yellow-700 ring-yellow-700/10 hover:bg-yellow-100",
  waiting_partner: "bg-orange-50 text-orange-700 ring-orange-700/10",
  waiting_confirmation: "bg-blue-50 text-blue-700 ring-blue-700/10",
  confirmed: "bg-green-50 text-green-700 ring-green-700/10",
};

const statusText: Record<string, string> = {
  completed: "Completed",
  withdrawn: "Withdrawn",
  payment_required: "Payment required",
  waiting_partner: "Waiting for partner",
  waiting_confirmation: "Waiting confirmation",
  confirmed: "Confirmed",
};

const statusTooltip: Record<string, string> = {
  completed: "This competition is completed",
  withdrawn: "You have withdrawn from this competition",
  payment_required: "Payment is required to confirm your entry",
  waiting_partner: "Waiting for your partner to join. Open for invite details.",
  waiting_confirmation: "Waiting for your entry to be confirmed",
  confirmed: "Your entry is confirmed",
};

const getPlacementText = (placement: number | null | undefined): string | null => {
  if (placement === null || placement === undefined) return null;
  const placementNum = placement + 1; // 0-indexed to 1-indexed
  if (placementNum === 1) return "1st Place";
  if (placementNum === 2) return "2nd Place";
  if (placementNum === 3) return "3rd Place";
  return `${placementNum}th Place`;
};

const UncontrolledCompetitionItem: React.FC<CompetitionItemProps> = ({ registration }) => {
  const competition = registration.competition;
  const tournamentSession = registration.session;
  const isPast = tournamentSession.completed_at
    ? new Date(tournamentSession.completed_at) < new Date()
    : false;
  const status = registration.computed_status;
  const placement = registration.placement;
  const partnerNames = registration.entry?.partner_names;

  // Get display status - show "In progress" instead of "Confirmed" when tournament session is in registration or active
  const sessionStatus = tournamentSession.status;
  const isTournamentActive = sessionStatus === "registration" || sessionStatus === "active";
  const displayStatusText = status === "confirmed" && isTournamentActive ? "In progress" : statusText[status];
  const displayStatusTooltip =
    status === "confirmed" && isTournamentActive ? "The tournament is in progress" : statusTooltip[status];

  // For past tournaments, show completed date; for future, show start date
  const displayDate =
    isPast && tournamentSession?.completed_at
      ? tournamentSession.completed_at
      : tournamentSession?.starts_at || tournamentSession?.ends_at;

  const [date, time] = displayDate ? dateAndTime(displayDate) : ["", ""];

  const manageUrl = tournamentSession?.portal_slug
    ? `/in/tournaments/${tournamentSession.portal_slug}/manage/${competition.id}`
    : null;

  const content = (
    <div className="flex flex-col gap-4">
      {/* Header: Title and Status */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {tournamentSession?.name && (
            <div className="mb-1 text-sm font-medium text-slate-500">{tournamentSession.name}</div>
          )}
          <h3 className="text-lg font-semibold leading-tight text-slate-900">
            {competition.name || "Unnamed competition"}
          </h3>
          {placement !== null && placement !== undefined && (
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200">
                {getPlacementText(placement)}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center flex-shrink-0 gap-2">
          <span
            className={`${statusColor[status]} inline-flex items-center whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium ring-1 ring-inset`}
          >
            {displayStatusText}
          </span>
          <Tooltip icon>{displayStatusTooltip}</Tooltip>
        </div>
      </div>

      {/* Details: Date/Time and Partner */}
      <div className="flex flex-col gap-2.5 border-t border-slate-100 pt-3">
        {displayDate && (
          <div className="flex items-center gap-2.5 text-sm text-slate-600">
            <CalendarIcon className="flex-shrink-0 w-4 h-4 text-slate-400" />
            <span>
              {isPast && tournamentSession?.completed_at ? (
                <span>Completed: {formatDate(tournamentSession.completed_at)}</span>
              ) : (
                <>
                  <span className="font-medium">{date}</span>
                  <span className="text-slate-500"> @ {time}</span>
                </>
              )}
            </span>
          </div>
        )}
        {partnerNames && partnerNames.length > 0 && (
          <div className="flex items-center gap-2.5 text-sm text-slate-600">
            <UserIcon className="flex-shrink-0 w-4 h-4 text-slate-400" />
            <span>
              <span className="text-slate-500">Partner{partnerNames.length > 1 ? "s" : ""}: </span>
              <span className="font-medium">{partnerNames.join(", ")}</span>
            </span>
          </div>
        )}
      </div>

      <div className="flex justify-end pt-3 text-blue-500 border-t border-slate-100 hover:underline">
        View details →
      </div>
    </div>
  );

  return (
    <div className="relative w-full overflow-hidden text-sm transition-all bg-white border rounded-lg shadow-sm group border-slate-200 hover:border-slate-300 hover:shadow-md">
      {manageUrl ? (
        <Link to={manageUrl} className="block p-5">
          {content}
        </Link>
      ) : (
        <div className="p-5">{content}</div>
      )}
    </div>
  );
};

const CompetitionItem = ApplicationView(UncontrolledCompetitionItem);
export { CompetitionItem };
